import {Card, Container, Group, Menu, rem, Stack, Text, Title, UnstyledButton} from "@mantine/core";
import {useLocation, useNavigate} from "react-router-dom";
import {
  activityPeriodSelector,
  ttasSelector,
  useGetOperatorActivityPeriodQuery,
  useGetTtasByActivityPeriodQuery
} from "@ots-frontend/ots-data";
import {skipToken} from "@reduxjs/toolkit/query";
import {useCallback, useMemo} from "react";
import {ErrorPage} from "../../components/page/ErrorPage/ErrorPage";
import {LoadingPage} from "../../components/page/LoadingPage/LoadingPage";
import {ThemedPage} from "../../components/page/ThemedPage/ThemedPage";
import {useApp} from "../../providers/RegionProvider/RegionProvider";
import {IconChevronDown} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {useDownloadTicketsReport} from "../../hooks/useDownloadTickets";
import {ResizeableContent} from "../../components/ResizableContent/ResizableContent";
import {RouteBreadcrumbs} from "../../components/breadcrumb/RouteBreadcrumbs/RouteBreadcrumbs";
import {TicketDataTable} from "../../components/data-table/TicketDataTable/TicketDataTable";
import {PAGINATION_PAGE_SIZES} from "../../components/data-table/DataTable/usePagination";
import {
  FormattedActivityPeriodDate
} from "../../components/date/FormattedActivityPeriodDate/FormattedActivityPeriodDate";

type Props = {
  id?: string;
}

export const ActivityPeriodTicketDataTablePageRoute = () => {
  const {state} = useLocation();
  const id = state?.id;
  const navigate = useNavigate();

  if (!id) {
    navigate(-1);
    return (
      <ErrorPage message={"Activity Period Not Found"}/>
    );
  }

  return <ActivityPeriodTicketDataTablePage id={id}/>;
};

export const ActivityPeriodTicketDataTablePage = (props: Props) => {
  const {id} = props;

  const {baseQueryApi} = useApp()
  const {t} = useTranslation(["translation", "common"]);

  const selectActivityPeriod = useMemo(() => {
    return activityPeriodSelector()
  }, []);

  const {activityPeriod, isFetching} = useGetOperatorActivityPeriodQuery(id ?? skipToken, {
    selectFromResult: (result) => ({
      activityPeriod: selectActivityPeriod(result),
      isFetching: result.isFetching
    })
  });

  const selectTickets = useMemo(() => {
    return ttasSelector();
  }, []);

  const {
    tickets,
    isFetchingTickets,
    refetch
  } = useGetTtasByActivityPeriodQuery(activityPeriod?.id ? {activityPeriodId: activityPeriod.id} : skipToken, {
    selectFromResult: (result) => ({
      ...result,
      tickets: selectTickets(result, {}),
      isFetchingTickets: result.isFetching,
    })
  });

  const {
    downloadTicketsReport,
  } = useDownloadTicketsReport({baseQueryApi})

  const onExportClick = useCallback(() => {
    downloadTicketsReport(tickets)
  }, [downloadTicketsReport, tickets])

  if (isFetching) {
    return (
      <LoadingPage/>
    );
  }

  if (!activityPeriod) {
    return (
      <ErrorPage message={"Activity Period Not Found"}/>
    );
  }

  return (
    <ThemedPage>
      {(classes) => (
        <ResizeableContent>
          {(size) => (
            <Container h={size.height} w={size.width} className={classes.container} fluid={true} p="md">
              <Stack h="100%" m="sm">
                <RouteBreadcrumbs/>
                <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                  <Group position="apart">
                    <Title order={4}>
                      {t("common.entity.activityperiod", "Activity Period")}
                      {" "}
                      {t("common.entity.tta.alt.plural", "Tickets")}
                      {" - "}
                      <FormattedActivityPeriodDate
                        fromDate={activityPeriod.fromDttm}
                        toDate={activityPeriod.toDttm}
                        reportTimeType={activityPeriod.type}/>
                      {t("page.tickets.title", "Tickets", {ns: "translation"})}
                    </Title>
                    <Menu
                      width={260}
                      position="bottom-end"
                      transitionProps={{transition: "pop-top-right"}}
                      withinPortal
                    >
                      <Menu.Target>
                        <UnstyledButton>
                          <Group spacing={7}>
                            <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                              {t('common.action.plural', "Actions")}
                            </Text>
                            <IconChevronDown size={rem(12)} stroke={1.5}/>
                          </Group>
                        </UnstyledButton>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item onClick={() => refetch()}>
                          {t("actions.refresh", "Refresh", {ns: "common"})}
                        </Menu.Item>
                        <Menu.Item onClick={() => onExportClick()}>
                          {t("actions.export", "Export", {ns: "common"})}
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Group>
                </Card>
                <TicketDataTable
                  withBorder
                  records={tickets}
                  recordsPerPageOptions={PAGINATION_PAGE_SIZES}
                  fetching={isFetchingTickets}
                  ticketUrlPrefix={"/trucking"}/>
              </Stack>
            </Container>
          )}
        </ResizeableContent>
      )}
    </ThemedPage>
  );
};
